/**
 * API catalogue paramétrable — formats × framings × supports.
 *
 * Les 3 entités sont symétriques (CRUD identique). Les fonctions retournent
 * des promesses typées sur `types/catalog.ts` (auto-générés depuis OpenAPI).
 *
 * DELETE renvoie 409 Conflict si l'entrée est référencée par une variante
 * (FK RESTRICT côté BE) — le composant appelant convertit en `useConfirm()`
 * d'erreur ou affiche le message tel quel.
 */

import { apiClient } from './client'
import type {
  EmotionalIntentCreateRequest,
  EmotionalIntentResponse,
  EmotionalIntentUpdateRequest,
  FormatCreateRequest,
  FormatResponse,
  FormatUpdateRequest,
  FramingCreateRequest,
  FramingResponse,
  FramingUpdateRequest,
  SlugSuggestRequest,
  SlugSuggestResponse,
  SupportCreateRequest,
  SupportResponse,
  SupportUpdateRequest,
  TargetAudienceCreateRequest,
  TargetAudienceResponse,
  TargetAudienceUpdateRequest,
} from '@/types/catalog'

interface ListParams {
  only_active?: boolean
}

export const catalogApi = {
  // ── Formats ─────────────────────────────────────────────────────────
  listFormats: async (params: ListParams = {}): Promise<FormatResponse[]> => {
    const { data } = await apiClient.get<FormatResponse[]>('/catalog/formats', {
      params,
    })
    return data
  },
  createFormat: async (payload: FormatCreateRequest): Promise<FormatResponse> => {
    const { data } = await apiClient.post<FormatResponse>('/catalog/formats', payload)
    return data
  },
  updateFormat: async (id: number, payload: FormatUpdateRequest): Promise<FormatResponse> => {
    const { data } = await apiClient.patch<FormatResponse>(`/catalog/formats/${id}`, payload)
    return data
  },
  deleteFormat: async (id: number): Promise<void> => {
    await apiClient.delete(`/catalog/formats/${id}`)
  },

  // ── Framings ────────────────────────────────────────────────────────
  listFramings: async (params: ListParams = {}): Promise<FramingResponse[]> => {
    const { data } = await apiClient.get<FramingResponse[]>('/catalog/framings', {
      params,
    })
    return data
  },
  createFraming: async (payload: FramingCreateRequest): Promise<FramingResponse> => {
    const { data } = await apiClient.post<FramingResponse>('/catalog/framings', payload)
    return data
  },
  updateFraming: async (id: number, payload: FramingUpdateRequest): Promise<FramingResponse> => {
    const { data } = await apiClient.patch<FramingResponse>(`/catalog/framings/${id}`, payload)
    return data
  },
  deleteFraming: async (id: number): Promise<void> => {
    await apiClient.delete(`/catalog/framings/${id}`)
  },

  // ── Supports ────────────────────────────────────────────────────────
  listSupports: async (params: ListParams = {}): Promise<SupportResponse[]> => {
    const { data } = await apiClient.get<SupportResponse[]>('/catalog/supports', {
      params,
    })
    return data
  },
  createSupport: async (payload: SupportCreateRequest): Promise<SupportResponse> => {
    const { data } = await apiClient.post<SupportResponse>('/catalog/supports', payload)
    return data
  },
  updateSupport: async (id: number, payload: SupportUpdateRequest): Promise<SupportResponse> => {
    const { data } = await apiClient.patch<SupportResponse>(`/catalog/supports/${id}`, payload)
    return data
  },
  deleteSupport: async (id: number): Promise<void> => {
    await apiClient.delete(`/catalog/supports/${id}`)
  },

  // ── Emotional intents (Tier 2 — paramétrable S2.5c) ─────────────────
  listEmotionalIntents: async (params: ListParams = {}): Promise<EmotionalIntentResponse[]> => {
    const { data } = await apiClient.get<EmotionalIntentResponse[]>('/catalog/emotional-intents', {
      params,
    })
    return data
  },
  createEmotionalIntent: async (
    payload: EmotionalIntentCreateRequest,
  ): Promise<EmotionalIntentResponse> => {
    const { data } = await apiClient.post<EmotionalIntentResponse>(
      '/catalog/emotional-intents',
      payload,
    )
    return data
  },
  updateEmotionalIntent: async (
    id: number,
    payload: EmotionalIntentUpdateRequest,
  ): Promise<EmotionalIntentResponse> => {
    const { data } = await apiClient.patch<EmotionalIntentResponse>(
      `/catalog/emotional-intents/${id}`,
      payload,
    )
    return data
  },
  deleteEmotionalIntent: async (id: number): Promise<void> => {
    await apiClient.delete(`/catalog/emotional-intents/${id}`)
  },

  // ── Target audiences (Tier 2 — paramétrable S2.5c) ──────────────────
  listTargetAudiences: async (params: ListParams = {}): Promise<TargetAudienceResponse[]> => {
    const { data } = await apiClient.get<TargetAudienceResponse[]>('/catalog/target-audiences', {
      params,
    })
    return data
  },
  createTargetAudience: async (
    payload: TargetAudienceCreateRequest,
  ): Promise<TargetAudienceResponse> => {
    const { data } = await apiClient.post<TargetAudienceResponse>(
      '/catalog/target-audiences',
      payload,
    )
    return data
  },
  updateTargetAudience: async (
    id: number,
    payload: TargetAudienceUpdateRequest,
  ): Promise<TargetAudienceResponse> => {
    const { data } = await apiClient.patch<TargetAudienceResponse>(
      `/catalog/target-audiences/${id}`,
      payload,
    )
    return data
  },
  deleteTargetAudience: async (id: number): Promise<void> => {
    await apiClient.delete(`/catalog/target-audiences/${id}`)
  },

  // ── Slug suggestion via Claude (Tier 2 only) ────────────────────────
  suggestSlug: async (payload: SlugSuggestRequest): Promise<SlugSuggestResponse> => {
    const { data } = await apiClient.post<SlugSuggestResponse>('/catalog/slug-suggest', payload)
    return data
  },
}
